// CONSTANTS for validation error types
export enum ValidationErrors {
  MISSING = 'MISSING',
  TYPE_MISMATCH = 'TYPE_MISMATCH',
  PATTERN_MISMATCH = 'PATTERN_MISMATCH',
  PASSWORD_MISMATCH = 'PASSWORD_MISMATCH',
  TOO_LONG = 'TOO_LONG',
  TOO_SHORT = 'TOO_SHORT',
  UNKNOWN = 'UNKNOWN',
  MISSING_FORM_SUB_NODES = 'MISSING_FORM_SUB_NODES',
  MISSING_CONDITION_SUB_NODES = 'MISSING_CONDITION_SUB_NODES',
  MISSING_CONDITION_NODE_SUB_NODES = 'MISSING_CONDITION_NODE_SUB_NODES',
  MISSING_DROP_DOWN_SUB_NODES = 'MISSING_DROP_DOWN_SUB_NODES',
  MISSING_SHORTCUT_INPUT = 'MISSING_SHORTCUT_INPUT',
  MISSING_TRANSFER_ENDPOINT = 'MISSING_TRANSFER_ENDPOINT',
  MISSING_AUTOMATOR = 'MISSING_AUTOMATOR',
  MISSING_NAME = 'MISSING_NAME',
  MISSING_LABEL = 'MISSING_LABEL',
  MISSING_VALUE = 'MISSING_VALUE',
  MISSING_CONTENT = 'MISSING_CONTENT',
  MISSING_LINK = 'MISSING_LINK',
}

// validation config for single record property
export type ValidationConfig = {
  isRequired?: boolean;
  type?: string;
  pattern?: RegExp;
  minLength?: number;
  maxLength?: number;
};

// map of validation configuration
export type ValidationConfigs = Record<string, ValidationConfig>;

// Map of props & validation errors
// key - name of property of record
// error - validation error type
export type ValidationState = Record<string, ValidationErrors>;

/**
 * Provide functionality to validate user input with given config.
 */
export const Validation = {
  /**
   * Validate given record for config.
   *
   * @param record - record to validate
   * @param validationConfigs - validation config to use
   *
   * @returns {ValidationState} - errors
   */
  validateRecordWithConfig: <R>(
    record: R,
    validationConfigs?: ValidationConfigs,
  ): ValidationState => {
    // if no config is available, no errors
    if (validationConfigs == null) {
      return {};
    }

    const recordObject = { ...record };
    let result: ValidationState = {};

    // loop through record props
    Object.keys(recordObject).forEach((key) => {
      // check if there is a validation entry for the key
      const pattern = validationConfigs[key];
      if (pattern == null) {
        return;
      }

      // validate single entry
      const value = recordObject[key];
      const error = Validation.validateValueWithConfig(value, pattern);

      // if value is invalid, store in result
      if (error != null) {
        result[key] = error;
      }
    });

    return result;
  },

  /**
   * Validate given value with config.
   *
   * @param value - value to validate
   * @param config - config for validation
   *
   * @returns {ValidationErrors|null} - error or null
   */
  validateValueWithConfig: <T>(value: T, config: ValidationConfig): ValidationErrors | null => {
    // if prop is required, check it's not empty
    if (config.isRequired) {
      // @ts-ignore length does not exist on every type
      if (value == null || value === '' || value === 0 || value.length === 0) {
        return ValidationErrors.MISSING;
      }
    }

    // if props needs to be a specific type
    // check for type
    if (config.type != null) {
      if (typeof value !== config.type) {
        return ValidationErrors.TYPE_MISMATCH;
      }
    }

    // validate string input
    // all text/email input fields are strings
    if (typeof value === 'string') {
      // check for min length
      if (config.minLength != null && value.length < config.minLength) {
        return ValidationErrors.TOO_SHORT;
      }

      // check for max length
      if (config.maxLength != null && value.length > config.maxLength) {
        return ValidationErrors.TOO_LONG;
      }

      // check for regex
      if (config.pattern != null && !config.pattern.test(value)) {
        return ValidationErrors.PATTERN_MISMATCH;
      }
    }

    return null;
  },
};
